import { createContext, useContext, type ReactNode } from "react";
import type { ProductResponse } from "@/app/api/types/product";
import type { StockSummary } from "@/query/hooks/useGlobalStocks";
import type { LotResponse } from "@/app/api/lots/client";
import type { LocationResponse } from "@/app/api/types/location";
import type { CustomFieldDefinition } from "@/app/api/types/featureFlags";
import type { EffectivePrice } from "@/query/hooks/useGlobalPrices";

/**
 * Per-pre-order lookup bundle for product price + stock display.
 *
 * Mounted once at FlowsTable level so every row (placeholder, editor,
 * empty-add row) reads via a single useContext call instead of each cell
 * re-subscribing to the same global caches. Stays lazy at the right
 * boundary: collapsed partner cards don't mount FlowsTable, so this
 * provider only exists for pre-orders the user has opened.
 */
export interface ProductLookupsContextValue {
  showStocks: boolean;
  showPrices: boolean;
  /** Location-scoped stockMap when preOrder.location_id is set, otherwise global. */
  stockMap: Map<string, StockSummary>;
  /** Resolves the effective price for a (product, partner) pair using the
   *  user-partner → user-group → company-partner → company-group → default
   *  product-price hierarchy. Pure memory lookup, no I/O. */
  getEffectivePrice: (
    productId: string | null,
    partnerId: string | null,
  ) => number | null;
  /**
   * Source-aware variant of `getEffectivePrice`. Returns `{ value, source }`
   * so the recap PriceCell can render a phantom preview alongside a
   * TARIF/LISTE source pill without re-deriving the bucket from the value.
   * Same hierarchy as `getEffectivePrice`; `null` when no tier resolves.
   */
  getEffectivePriceWithSource: (
    productId: string | null,
    partnerId: string | null,
  ) => EffectivePrice | null;
  unitMap: Map<string, string>;
  /** Catalog products keyed by id AND by child id. Cells call `.get(id)` once
   *  instead of each one re-subscribing to the Dexie liveQuery + walking the
   *  full product array. */
  productMap: Map<string, ProductResponse>;
  /** Same products as a stable array — used by handlers like
   *  `resolveProductDefaults` that take an array. */
  products: ProductResponse[];
  /**
   * `products` reduced to the product picker's corpus: non-variant and awake.
   * Derived once per FlowsTable (`selectNonVariantProducts`) so
   * `ProductSelectCombo` can take it as `allProductsOverride` instead of each
   * instance running its own catalog query + filter + search-index warm-up
   * (STK-1863).
   */
  nonVariantProducts: ProductResponse[];
  /**
   * Global lots list — mounted once at FlowsTable level via `useLots()`
   * so every ProductCellEditor reads from one TanStack Query subscription
   * instead of N (one per flow row). Cells that need lot data filter this
   * array by `flow.product_id` locally.
   */
  lots: LotResponse[];
  /**
   * Location list — sourced from the offline-first
   * `useRecapLocationsOffline()` hook at FlowsTable level. Cells read the
   * full list and look up by id locally rather than each one re-subscribing
   * to the same Dexie liveQuery.
   */
  locations: LocationResponse[];
  /**
   * Feature flag bundle — read once at FlowsTable level so cells don't
   * each hit `useFeatureFlag(...)` on mount. The flags are render-stable
   * for a given user/company session, so hoisting them away from the cell
   * boundary removes ~3 hook calls per flow row.
   */
  stockCustomFields: CustomFieldDefinition[] | undefined;
  pickLocationEnabled: boolean;
  isVivoMode: boolean;
}

const ProductLookupsContext = createContext<ProductLookupsContextValue | null>(
  null,
);

export function ProductLookupsProvider({
  value,
  children,
}: {
  value: ProductLookupsContextValue;
  children: ReactNode;
}) {
  return (
    <ProductLookupsContext.Provider value={value}>
      {children}
    </ProductLookupsContext.Provider>
  );
}

export function useProductLookups(): ProductLookupsContextValue | null {
  return useContext(ProductLookupsContext);
}
